import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import BuildTypePopup from '../recommendations/BuildTypePopup';

/**
 * JourneyModal - opens when a user clicks a top-level customer-journey
 * chip on the homepage. Renders a 2-step wizard inside ONE modal:
 *
 *   Step 1: sub-chip grid ("Where are you in the process?")
 *   Step 2: <BuildTypePopup embedded /> body (matchers + resources +
 *           explore CTA), all rendered inside the same modal frame
 *           with a "← Back" button to return to step 1.
 *
 * 2026-02-24: This replaces the previous double-modal stack where
 * clicking a sub-chip opened BuildTypePopup ON TOP of JourneyModal.
 * Single modal feels cleaner and avoids overlay z-index issues.
 *
 * External sub-chips (e.g. /help/building-disputes/...) still
 * navigate away rather than showing inline content.
 *
 * Props:
 *   journey - one entry from /data/journeyConfig.js JOURNEYS[]
 *   onClose - callback to dismiss the modal
 */
const JourneyModal = ({ journey, onClose }) => {
  const [activeChip, setActiveChip] = useState(null);  // { name, href, iconSrc }

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes; if we're on step 2 (a sub-chip is active), Esc backs
  // up to step 1 first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (activeChip) setActiveChip(null);
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, activeChip]);

  if (!journey) return null;

  // Title shown in the header reflects the current step.
  const headerTitle = activeChip ? activeChip.name : journey.name;
  const headerSubtitle = activeChip ? `Smart matches and key resources for ${activeChip.name.toLowerCase()}.` : journey.subtitle;
  const headerIcon = activeChip ? activeChip.iconSrc : journey.iconSrc;

  // 2026-02-24: Portal to document.body + z-[100] so this modal
  // escapes any parent stacking context AND sits above the sticky
  // header (z-[60]) and mega-menu (z-[70]). Previously the journey
  // modal mounted inline → the page header bled through the dimmed
  // backdrop on scroll.
  const node = (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid={`journey-modal-${journey.slug}`}
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-[#f0f0f3]">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            {activeChip && (
              <button
                type="button"
                onClick={() => setActiveChip(null)}
                className="p-1.5 -ml-1.5 rounded-full text-[#6e6e73] hover:text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors shrink-0"
                aria-label="Back"
                data-testid="journey-modal-back"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <img
              src={headerIcon}
              alt=""
              aria-hidden="true"
              className="w-14 h-14 sm:w-[60px] sm:h-[60px] object-contain shrink-0" loading="lazy" decoding="async" />
            <div className="min-w-0 text-left">
              <h2
                className="text-[19px] sm:text-[22px] font-semibold text-[#1d1d1f] leading-tight tracking-tight"
                data-testid={`journey-modal-title-${journey.slug}`}
              >
                {headerTitle}
              </h2>
              <p className="text-[13px] sm:text-[14px] text-[#6e6e73] mt-1 leading-snug">
                {headerSubtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-2 text-[#6e6e73] hover:text-[#1d1d1f] transition-colors shrink-0 self-start"
            aria-label="Close"
            data-testid="journey-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body - step 1 (sub-chip grid) OR step 2 (BuildTypePopup body) */}
        <div className="overflow-y-auto">
          {activeChip ? (
            // Step 2 - embed the existing BuildTypePopup INSIDE the same
            // modal frame. `embedded` prop strips the outer overlay/scrim/
            // header so only the body sections + footer render.
            <BuildTypePopup
              chip={activeChip}
              onClose={onClose}
              embedded
            />
          ) : (
            // Step 1 - sub-chip selection
            <>
              <div className="px-5 sm:px-6 pt-5 sm:pt-6">
                <p className="text-[14px] sm:text-[15px] font-medium text-[#1d1d1f]">
                  {journey.askQuestion}
                </p>
              </div>
              <div className="px-5 sm:px-6 pb-6 pt-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {journey.subChips.map((chip) => {
                    const id = chip.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    if (chip.external) {
                      return (
                        <Link
                          key={id}
                          to={chip.href}
                          onClick={onClose}
                          className="group flex items-center gap-3 p-3.5 rounded-xl border border-[#e8e8ed] hover:border-[#1d1d1f] hover:bg-[#fafafc] transition-all"
                          data-testid={`journey-subchip-${id}`}
                        >
                          <img
                            src={chip.iconSrc}
                            alt=""
                            aria-hidden="true"
                            className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" loading="lazy" decoding="async" />
                          <div className="flex-1 min-w-0 text-left">
                            <div className="text-[14px] sm:text-[15px] font-medium text-[#1d1d1f] leading-tight">
                              {chip.label}
                            </div>
                            {chip.when && (
                              <div className="text-[12px] text-[#6e6e73] mt-0.5">
                                {chip.when}
                              </div>
                            )}
                          </div>
                          <ArrowRight size={16} className="text-[#86868b] group-hover:text-[#1d1d1f] shrink-0" />
                        </Link>
                      );
                    }
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() =>
                          setActiveChip({
                            name: chip.label,
                            href: chip.href,
                            iconSrc: chip.iconSrc,
                          })
                        }
                        className="group flex items-center gap-3 p-3.5 rounded-xl border border-[#e8e8ed] hover:border-[#1d1d1f] hover:bg-[#fafafc] transition-all text-left"
                        data-testid={`journey-subchip-${id}`}
                      >
                        <img
                          src={chip.iconSrc}
                          alt=""
                          aria-hidden="true"
                          className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0" loading="lazy" decoding="async" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] sm:text-[15px] font-medium text-[#1d1d1f] leading-tight">
                            {chip.label}
                          </div>
                          {chip.when && (
                            <div className="text-[12px] text-[#6e6e73] mt-0.5">
                              {chip.when}
                            </div>
                          )}
                        </div>
                        <ArrowRight size={16} className="text-[#86868b] group-hover:text-[#1d1d1f] shrink-0" />
                      </button>
                    );
                  })}
                </div>

                {/* SEO landing link */}
                <div className="mt-5 pt-5 border-t border-[#f0f0f3]">
                  <Link
                    to={`/journey/${journey.slug}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 text-[13px] text-[#1d1d1f] hover:underline"
                    data-testid={`journey-explore-${journey.slug}`}
                  >
                    Read the full {journey.name.toLowerCase()} guide
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render via portal at document.body so parent transforms / z-stacks
  // can't trap the modal beneath the sticky header.
  if (typeof document === 'undefined') return node;
  return ReactDOM.createPortal(node, document.body);
};

export default JourneyModal;
